/**
 * The map under construction: the lists that become a `MissionDef`, the id counter, and
 * the scratch mask every placement is checked against. Footprints come from the sim's own
 * prop table (`collidersFor`), so "fits" here means the same thing as "does not overlap" there.
 */

import { collidersFor, PROPS } from '../sim/props';
import type {
  CaptiveDef, MineFieldDef, MissionDef, Polyline, PropDef, PropKind, Rect, SpawnDef, WaterBarrierDef,
} from '../sim/types';
import { FLAG, Mask } from './grid';

export interface Shape {
  length?: number;
  scale?: number;
  yaw?: number;
}

export interface FitOptions extends Shape {
  /** Clear ground demanded around the footprint, metres. */
  margin?: number;
  /** Flags the footprint must not touch. */
  avoid?: number;
}

/** Ground nothing solid may be put on. */
export const NO_BUILD = FLAG.solid | FLAG.road | FLAG.trench | FLAG.water | FLAG.keep | FLAG.mine;
/** Ground a soldier may not stand on. */
export const NO_STAND = FLAG.solid | FLAG.trench | FLAG.water | FLAG.mine;

/** The sim pads colliders by the ranger's radius plus a little when it builds the nav grid. */
const NAV_PAD = 0.5;

/** Two decimals are plenty for a map; `+ 0` turns -0 into 0, which JSON would otherwise not give back. */
export const round2 = (v: number): number => Math.round(v * 100) / 100 + 0;

export class MapBuilder {
  readonly sizeX: number;
  readonly sizeZ: number;
  readonly mask: Mask;
  readonly props: PropDef[] = [];
  readonly spawns: SpawnDef[] = [];
  readonly captives: CaptiveDef[] = [];
  readonly trenches: MissionDef['trenches'] = [];
  readonly roads: Polyline[] = [];
  readonly ponds: MissionDef['ponds'] = [];
  readonly waterBarriers: WaterBarrierDef[] = [];
  readonly mineFields: MineFieldDef[] = [];
  /** Anything the generator could not do; a non-empty list fails the attempt. */
  readonly problems: string[] = [];
  private nextPropId = 1;

  constructor(sizeX: number, sizeZ: number) {
    this.sizeX = sizeX;
    this.sizeZ = sizeZ;
    this.mask = new Mask(Math.ceil(sizeX), Math.ceil(sizeZ));
  }

  /** Bounding box of a prop's colliders; a point for props with no footprint (bridges). */
  bounds(kind: PropKind, x: number, z: number, shape: Shape = {}): Rect {
    const probe: PropDef = { id: 0, kind, x, z, yaw: shape.yaw ?? 0 };
    if (shape.length !== undefined) probe.length = shape.length;
    if (shape.scale !== undefined) probe.scale = shape.scale;
    const box: Rect = { x0: x, z0: z, x1: x, z1: z };
    for (const c of collidersFor(probe)) {
      const ex = c.shape === 'circle' ? c.r : c.hx;
      const ez = c.shape === 'circle' ? c.r : c.hz;
      box.x0 = Math.min(box.x0, c.x - ex);
      box.x1 = Math.max(box.x1, c.x + ex);
      box.z0 = Math.min(box.z0, c.z - ez);
      box.z1 = Math.max(box.z1, c.z + ez);
    }
    return box;
  }

  fits(kind: PropKind, x: number, z: number, o: FitOptions = {}): boolean {
    const b = this.bounds(kind, x, z, o);
    const m = o.margin ?? 0.6;
    return !this.mask.hitsRect(b.x0 - m, b.z0 - m, b.x1 + m, b.z1 + m, o.avoid ?? NO_BUILD);
  }

  /** Put a prop down without asking; callers that need room call `fits` or `place`. */
  add(kind: PropKind, x: number, z: number, extra: Partial<Omit<PropDef, 'id' | 'kind' | 'x' | 'z'>> = {}): PropDef {
    const prop: PropDef = { id: this.nextPropId++, kind, x: round2(x), z: round2(z), yaw: 0, ...extra };
    if (prop.length !== undefined) prop.length = round2(prop.length);
    if (prop.scale !== undefined) prop.scale = round2(prop.scale);
    prop.yaw = round2(prop.yaw);
    this.props.push(prop);
    const b = this.bounds(kind, prop.x, prop.z, prop);
    // Walk-through props (the hangar, the lever, bridges) reserve their ground instead of blocking it.
    const flag = PROPS[kind].blocksMove ? FLAG.solid : FLAG.keep;
    this.mask.rect(b.x0 - NAV_PAD, b.z0 - NAV_PAD, b.x1 + NAV_PAD, b.z1 + NAV_PAD, flag);
    return prop;
  }

  place(kind: PropKind, x: number, z: number, o: FitOptions & { target?: boolean } = {}): PropDef | null {
    if (!this.fits(kind, x, z, o)) return null;
    const extra: Partial<PropDef> = {};
    if (o.length !== undefined) extra.length = o.length;
    if (o.scale !== undefined) extra.scale = o.scale;
    if (o.yaw !== undefined) extra.yaw = o.yaw;
    if (o.target === true) extra.target = true;
    return this.add(kind, x, z, extra);
  }

  /** Register a soldier and keep his post clear of scatter. */
  spawn(s: SpawnDef): SpawnDef {
    const spawn: SpawnDef = { ...s, x: round2(s.x), z: round2(s.z), facing: round2(s.facing) };
    // Routes are copied: two soldiers on one beat must not share node arrays.
    if (s.patrol) spawn.patrol = s.patrol.map(([x, z]) => [round2(x), round2(z)] as [number, number]);
    this.spawns.push(spawn);
    this.mask.circle(spawn.x, spawn.z, 1.2, FLAG.keep);
    return spawn;
  }

  open(x: number, z: number, avoid: number = NO_STAND): boolean {
    return (this.mask.at(x, z) & avoid) === 0;
  }

  /** Centre of the nearest cell free of `avoid`, searching outward in square rings. */
  nearestOpen(x: number, z: number, reach = 4, avoid: number = NO_STAND): [number, number] | null {
    const cx = Math.floor(x);
    const cz = Math.floor(z);
    for (let ring = 0; ring <= reach; ring++) {
      let best: [number, number] | null = null;
      let bestD = Infinity;
      for (let oz = -ring; oz <= ring; oz++) {
        for (let ox = -ring; ox <= ring; ox++) {
          if (Math.max(Math.abs(ox), Math.abs(oz)) !== ring) continue;
          const px = cx + ox + 0.5;
          const pz = cz + oz + 0.5;
          if (px < 1.5 || pz < 1.5 || px > this.sizeX - 1.5 || pz > this.sizeZ - 1.5) continue;
          if (!this.open(px, pz, avoid)) continue;
          const d = (px - x) * (px - x) + (pz - z) * (pz - z);
          if (d < bestD) {
            bestD = d;
            best = [px, pz];
          }
        }
      }
      if (best) return best;
    }
    return null;
  }

  /** As `nearestOpen`, but the whole disc of `radius` round the spot must be free: room for a body, not just feet. */
  nearestClear(x: number, z: number, radius: number, reach: number, avoid: number): [number, number] | null {
    for (let ring = 0; ring <= reach; ring++) {
      for (let oz = -ring; oz <= ring; oz++) {
        for (let ox = -ring; ox <= ring; ox++) {
          if (Math.max(Math.abs(ox), Math.abs(oz)) !== ring) continue;
          const px = Math.floor(x) + ox + 0.5;
          const pz = Math.floor(z) + oz + 0.5;
          if (!this.mask.hitsCircle(px, pz, radius, avoid)) return [px, pz];
        }
      }
    }
    return null;
  }

  trench(points: Array<[number, number]>, width: number, depth: number): void {
    const rounded = points.map(([x, z]) => [round2(x), round2(z)] as [number, number]);
    this.trenches.push({ points: rounded, width: round2(width), depth: round2(depth) });
    this.mask.line(rounded, width / 2 + 1, FLAG.trench);
  }

  road(points: Array<[number, number]>, width: number): void {
    const rounded = points.map(([x, z]) => [round2(x), round2(z)] as [number, number]);
    this.roads.push({ points: rounded, width });
    this.mask.line(rounded, width / 2 + 0.8, FLAG.road);
  }

  mineField(rect: Rect, density: number, marked: boolean): void {
    const r: Rect = { x0: round2(rect.x0), z0: round2(rect.z0), x1: round2(rect.x1), z1: round2(rect.z1) };
    this.mineFields.push({ id: this.mineFields.length + 1, rect: r, density, marked });
    this.mask.rect(r.x0, r.z0, r.x1, r.z1, FLAG.mine);
  }
}
